import React, { FC, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Global } from './constants';
import { ConfigProvider, theme } from 'antd';

import NotFound from './pages/404.page';
import SuspenseWrapper from './hoc/suspense.hoc';
import ProtectedRoute from './hoc/protected-routes.hoc';
import AuthLayout from './components/layouts/auth-layout.component';
import AdminLayout from './components/layouts/admin-layout.component';
import CustomLayout from './components/layouts/custom-layout.component';
import { useTheme } from './contexts/theme.context';

import './scss/main.sass';

type Props = {
  isLoggedIn?: any,
}

const App: FC<Props> = ({ isLoggedIn }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const DashboardPage = React.lazy(() => import('./pages/admin/dashboard.page'));
  const ProfilePage = React.lazy(() => import('./pages/admin/profile.page'));
  const CompaniesPage = React.lazy(() => import('./pages/admin/branchs.page'));
  const AdminsPage = React.lazy(() => import('./pages/admin/admins.page'));
  const TemplatesPage = React.lazy(() => import('./pages/admin/templates.page'));
  const LoginPage = React.lazy(() => import('./pages/login.page'));
  const DocumentsPage = React.lazy(() => import('./pages/admin/documents.page'));
  const ReportsPage = React.lazy(() => import('./pages/admin/reports.page'));
  const DocumentCreatorPage = React.lazy(() => import('./pages/admin/document-creator.page'));
  const DocumentCreatorPageV2 = React.lazy(() => import('./pages/admin/document-creator-v2.page'));
  const VoboPage = React.lazy(() => import('./pages/vobo.page'));
  const VoboCompletedPage = React.lazy(() => import('./pages/completed.page'));
  const DocumentDetail = React.lazy(() => import('./pages/admin/document-detail.page'));
  const ResetPasswordPage = React.lazy(() => import('./pages/reset-password.page'));
  const ForgotPasswordPage = React.lazy(() => import('./pages/forgot-password.page'));
  const GenerateTokenPage = React.lazy(() => import('./pages/generate-token.page'));

  const { primaryColor, themeMode } = useTheme();

  useEffect(() => {
    const unprotectedRoutes = Global.UNPROTECTED_ROUTES;
    const path = window.location.pathname;

    const isUnprotected = unprotectedRoutes.some(route => path.startsWith(route));

    if (isUnprotected) {
      return;
    }

    if (!isLoggedIn) {
      navigate(Global.APP_URLS.LOGIN);
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    // Update body background and text color based on theme mode
    if (themeMode === 'dark') {
      document.documentElement.style.setProperty('--layout-body-bg', '#000000');
      document.documentElement.style.setProperty('--layout-header-bg', '#141414');
      document.documentElement.style.setProperty('--layout-sider-bg', '#141414');
      document.documentElement.style.setProperty('--component-bg', '#1f1f1f');
      document.documentElement.style.setProperty('--text-color', '#ffffff');
      document.documentElement.style.setProperty('--border-color', '#303030');

      document.body.style.backgroundColor = '#000000';
      document.body.style.color = '#ffffff';
    } else {
      document.documentElement.style.setProperty('--layout-body-bg', '#f0f2f5');
      document.documentElement.style.setProperty('--layout-header-bg', '#ffffff');
      document.documentElement.style.setProperty('--layout-sider-bg', '#ffffff');
      document.documentElement.style.setProperty('--component-bg', '#ffffff');
      document.documentElement.style.setProperty('--text-color', 'rgba(0, 0, 0, 0.85)');
      document.documentElement.style.setProperty('--border-color', '#f0f0f0');

      document.body.style.backgroundColor = '#f0f2f5';
      document.body.style.color = 'rgba(0, 0, 0, 0.85)';
    }
  }, [themeMode]);

  return (
    <ConfigProvider
      theme={{
        algorithm: themeMode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          colorPrimary: primaryColor,
          borderRadius: 8,
          fontFamily: '"Outfit", sans-serif',
        },
        components: {
          Card: {
            boxShadow: themeMode === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.4)' : '0 4px 12px rgba(0, 0, 0, 0.08)',
            borderRadius: 12,
          },
          Table: {
            headerBg: themeMode === 'dark' ? '#1f1f1f' : '#fafafa',
            headerColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
            rowHoverBg: themeMode === 'dark' ? '#262626' : '#f5f5f5',
            borderColor: '#f0f0f0',
            headerBorderRadius: 8,
          },
          Button: {
            borderRadius: 6,
            boxShadow: '0 2px 0 rgba(0, 0, 0, 0.045)',
          },
          Input: {
            borderRadius: 8,
          },
          Select: {
            borderRadius: 8,
          }
        },
      }}
    >
      <SuspenseWrapper>
        <Routes>
          <Route path="/" element={
            <ProtectedRoute route={location.pathname}>
              <AuthLayout />
            </ProtectedRoute>
          }>
            <Route index element={<LoginPage />} />
          </Route>
          <Route path="/auth" element={<AuthLayout />}>
            <Route path="reset-password" element={<ResetPasswordPage />} />
            <Route path="forgot" element={<ForgotPasswordPage />} />
            <Route path="generateToken" element={<GenerateTokenPage />} />
          </Route>
          <Route path="/admin" element={
            <ProtectedRoute route={location.pathname}>
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route index element={<DocumentsPage />} />
            <Route path="/admin/profile" element={<ProfilePage />} />
            <Route path="/admin/companies" element={<CompaniesPage />} />
            <Route path="/admin/admins" element={<AdminsPage />} />
            <Route path="/admin/templates" element={<TemplatesPage />} />
            <Route path="/admin/documents" element={<DocumentsPage />} />
            <Route path="/admin/document/creator" element={<DocumentCreatorPage />} />
            <Route path="/admin/document/creator-v2" element={<DocumentCreatorPageV2 />} />
            <Route path="/admin/document/:documentId" element={<DocumentDetail />} />
            <Route path="/admin/reports" element={<ReportsPage />} />
          </Route>
          <Route path="/document" element={<CustomLayout />}>
            <Route path="/document/vobo/:documentId" element={<VoboPage />} />
            <Route path="/document/complete-document/:documentId" element={<VoboCompletedPage />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </SuspenseWrapper>
    </ConfigProvider>
  );
};

const mapStateToProps = (state: any) => ({
  isLoggedIn: state.auth?.login || null,
});

export default connect(mapStateToProps, null)(App);
